import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link as RouterLink } from '~/lib/react-router-dom';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useGetRewardsInventory, useUpsertRewardsInventory } from '~/hooks/apis/rewards/useRewardsInventory';

import type { RewardsTier } from '~/services/RewardsService';

/** Per-customer tier display order and labels (excludes the synthetic 'total' tier). */
const PER_TIER: Array<{ key: Exclude<RewardsTier, 'total'>; label: string }> = [
  { key: 'bronze', label: 'Bronze' },
  { key: 'silver', label: 'Silver' },
  { key: 'gold', label: 'Gold' },
  { key: 'platinum', label: 'Platinum' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  entryId: string | null;
  entryLabel: string;
}

/**
 * Dialog for configuring inventory quantities for a reward entry.
 *
 * Inventory is two-level:
 *  - An entry-level "total" cap (max redemptions across all tiers combined).
 *  - Per-tier caps (max redemptions for each customer tier).
 *
 * The runtime aggregate cap (total) is enforced by the server at reserve time
 * and may be tighter than the sum of per-tier caps; this is intentional and
 * lets admins provision per-tier caps generously without committing to selling
 * the sum.
 *
 * For a brand-new entry, typing a value in the Total field auto-fills the four
 * per-tier fields with the same value, until the admin manually edits any
 * per-tier field (at which point the auto-link breaks).
 */
export default function ClassicRewardsInventoryDialog({
  open,
  onClose,
  entryId,
  entryLabel,
}: Props): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { data: inventory, refetch, isFetching: isInventoryFetching } = useGetRewardsInventory(open ? entryId : null);
  const upsertInventory = useUpsertRewardsInventory();

  const [total, setTotal] = useState<number>(0);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  /** Once true, editing Total no longer cascades into per-tier fields. */
  const [tiersTouched, setTiersTouched] = useState<boolean>(false);
  /** Tracks whether local state has been synced from the latest fetched inventory. */
  const [isQuantitiesSynced, setIsQuantitiesSynced] = useState(false);

  /** Refetch inventory every time the dialog opens to get the latest numbers. */
  useEffect(() => {
    if (open && entryId) {
      refetch();
    }
  }, [open, entryId, refetch]);

  /** Reset local state on entry change so we don't carry stale values across entries. */
  useEffect(() => {
    setTotal(0);
    setQuantities({});
    setTiersTouched(false);
    setIsQuantitiesSynced(false);
  }, [entryId]);

  /** Sync local state from fetched inventory data. */
  useEffect(() => {
    if (!inventory) {
      return;
    }
    const initialQuantities: Record<string, number> = {};
    for (const row of inventory) {
      if (row.tier === 'total') {
        setTotal(row.totalQuantity);
        continue;
      }
      initialQuantities[row.tier] = row.totalQuantity;
    }
    for (const tier of PER_TIER) {
      if (initialQuantities[tier.key] === undefined) {
        initialQuantities[tier.key] = 0;
      }
    }
    setQuantities(initialQuantities);
    // Any existing inventory (persisted tier rows of any value, or just a total
    // row) means this is not a brand-new entry — disable auto-seed so editing
    // Total later doesn't clobber persisted tier caps, including deliberate zeros.
    setTiersTouched(inventory.length > 0);
    setIsQuantitiesSynced(true);
  }, [inventory]);

  // ---- Derived helpers ----

  /** Per-tier rows from the server (read-only operational data). */
  const perTierRows = useMemo(() => (inventory ?? []).filter((r) => r.tier !== 'total'), [inventory]);

  /** SUM of reserved + redeemed across the four per-tier rows. */
  const perTierCommittedSum = useMemo(
    () => perTierRows.reduce((s, r) => s + r.reservedCount + r.redeemedCount, 0),
    [perTierRows],
  );

  /**
   * Path to the reservations dashboard, pre-filtered by this entry. Used by
   * the "View reservations" affordances surfaced when any tier has reserved
   * or redeemed activity. We don't filter by tier — the dashboard shows all
   * reservations against the entry so admins can compare across tiers.
   */
  const reservationsHref = entryId ? `/societe/classic-rewards?catalogEntryId=${entryId}` : '';

  /** Remaining capacity at the entry level given the current Total value. */
  const totalRemaining = total - perTierCommittedSum;

  /**
   * True when the entry is being configured from scratch — no inventory rows
   * returned from the API. Drives "Total must be set before per-tier inputs
   * become available" UX.
   */
  const isBrandNewEntry = (inventory ?? []).length === 0;

  /**
   * Per-tier inputs are enabled when either:
   *  - The entry already has inventory configured (existing entry — admin can
   *    edit tier caps independently of Total), OR
   *  - The brand-new entry has had Total set to a positive value (tier inputs
   *    light up after the Total is set).
   */
  const tierInputsEnabled = !isBrandNewEntry || total > 0;

  // ---- Validation ----

  const totalError = total < perTierCommittedSum;
  const hasTierError = PER_TIER.some(({ key }) => {
    const row = perTierRows.find((i) => i.tier === key);
    const minAllowed = (row?.reservedCount ?? 0) + (row?.redeemedCount ?? 0);
    return (quantities[key] ?? 0) < minAllowed;
  });
  /**
   * Total must be > 0 to save — the backend rejects per-tier caps without a
   * positive entry total. Mirrored here so the Save button surfaces the
   * constraint instead of the user discovering it via a backend rejection.
   */
  const totalMustBePositive = total <= 0;
  const hasValidationError = totalError || hasTierError || totalMustBePositive;

  // ---- Handlers ----

  /** Total field edit: cascades to per-tier fields while tiersTouched is false. */
  const onTotalChange = (raw: string) => {
    const next = Math.max(0, parseInt(raw, 10) || 0);
    setTotal(next);
    if (!tiersTouched) {
      const seeded: Record<string, number> = {};
      for (const tier of PER_TIER) {
        seeded[tier.key] = next;
      }
      setQuantities(seeded);
    }
  };

  /** Per-tier field edit: breaks the auto-link going forward. */
  const onTierChange = (tier: string, raw: string) => {
    const next = Math.max(0, parseInt(raw, 10) || 0);
    setQuantities((prev) => ({ ...prev, [tier]: next }));
    if (!tiersTouched) {
      setTiersTouched(true);
    }
  };

  /** Saves the total + per-tier rows in one upsert call. */
  const handleSave = async () => {
    if (!entryId) {
      return;
    }
    const tiers: Array<{ tier: RewardsTier; totalQuantity: number }> = [
      { tier: 'total', totalQuantity: total },
      ...PER_TIER.map(({ key }) => ({ tier: key, totalQuantity: quantities[key] ?? 0 })),
    ];
    try {
      await upsertInventory.mutateAsync({ catalogEntryId: entryId, tiers });
      enqueueSnackbar('Inventory saved successfully.', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save inventory.', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Inventory — {entryLabel}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the entry-level total cap and per-tier caps. The total is the maximum number of redemptions across
          all tiers combined. Per-tier caps may sum to more than the total; the lower of the two limits applies at
          booking time.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <TextField
            label="Total inventory"
            type="number"
            size="small"
            value={total}
            error={totalError}
            helperText={
              totalError
                ? `Min ${perTierCommittedSum} (sum of reserved + redeemed across all tiers)`
                : `Remaining: ${Math.max(0, totalRemaining)}`
            }
            onChange={(e) => onTotalChange(e.target.value)}
            inputProps={{ min: perTierCommittedSum }}
            sx={{ width: 220 }}
          />
          {perTierCommittedSum > 0 && reservationsHref && (
            <Link
              component={RouterLink}
              to={reservationsHref}
              target="_blank"
              rel="noopener"
              underline="hover"
              data-testid="view-reservations-total"
            >
              View reservations
            </Link>
          )}
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tier</TableCell>
              <TableCell align="right">Cap</TableCell>
              <TableCell align="right">Reserved</TableCell>
              <TableCell align="right">Redeemed</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {PER_TIER.map(({ key, label }) => {
              const row = perTierRows.find((i) => i.tier === key);
              const cap = quantities[key] ?? 0;
              const reserved = row?.reservedCount ?? 0;
              const redeemed = row?.redeemedCount ?? 0;
              const tierRemaining = cap - reserved - redeemed;
              const available = Math.max(0, Math.min(tierRemaining, totalRemaining));
              const minAllowed = reserved + redeemed;
              const isBelowMin = cap < minAllowed;
              const hasReservations = reserved + redeemed > 0;

              return (
                <TableRow key={key}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={cap}
                      error={isBelowMin}
                      disabled={!tierInputsEnabled}
                      helperText={
                        isBelowMin
                          ? `Min ${minAllowed} (reserved + redeemed)`
                          : !tierInputsEnabled
                          ? 'Set Total first'
                          : undefined
                      }
                      onChange={(e) => onTierChange(key, e.target.value)}
                      inputProps={{ min: minAllowed }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right">{reserved}</TableCell>
                  <TableCell align="right">{redeemed}</TableCell>
                  <TableCell align="right">{available}</TableCell>
                  <TableCell align="right">
                    {hasReservations && reservationsHref && (
                      <Link
                        component={RouterLink}
                        to={reservationsHref}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        data-testid={`view-reservations-${key}`}
                      >
                        View
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Tier caps may sum to more than the entry total; the lower of the two limits applies at booking time.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={upsertInventory.isPending}
          disabled={hasValidationError || isInventoryFetching || !isQuantitiesSynced}
        >
          Save Inventory
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
